/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AsyncFunctionResponse } from "../models/AsyncFunctionResponse";
import type { CustomerDetailResponse } from "../models/CustomerDetailResponse";
import type { CustomerExistsResponse } from "../models/CustomerExistsResponse";
import type { CustomerFeatures } from "../models/CustomerFeatures";
import type { CustomerFeaturesPartialUpdate } from "../models/CustomerFeaturesPartialUpdate";
import type { CustomerListResponse } from "../models/CustomerListResponse";
import type { CustomerOrderLimitsResponse } from "../models/CustomerOrderLimitsResponse";
import type { CustomerOrderLimitsSetRequest } from "../models/CustomerOrderLimitsSetRequest";
import type { CustomerSearchListResponse } from "../models/CustomerSearchListResponse";
import type { CustomerUserCollection } from "../models/CustomerUserCollection";
import type { EnableBalancesRequest } from "../models/EnableBalancesRequest";
import type { KybRequest } from "../models/KybRequest";
import type { OnboardingStateResponse } from "../models/OnboardingStateResponse";
import type { SignedUrlResponse } from "../models/SignedUrlResponse";
import type { UpdateCustomerSegmentRequest } from "../models/UpdateCustomerSegmentRequest";
import type { UpdateCustomerSegmentResponse } from "../models/UpdateCustomerSegmentResponse";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class CustomerService {
  /**
   * Get customer list
   * @returns CustomerListResponse OK
   * @throws ApiError
   */
  public static getCustomerList(): CancelablePromise<CustomerListResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customer",
      errors: {
        401: `Unauthorized`,
        404: `No customers found`,
      },
    });
  }

  /**
   * Update an existing customer with optional survey info
   * @param requestBody
   * @returns UpdateCustomerSegmentResponse OK
   * @throws ApiError
   */
  public static updateCustomerSegment(
    requestBody: UpdateCustomerSegmentRequest
  ): CancelablePromise<UpdateCustomerSegmentResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/customer",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Submit KYB information for a customer
   * @param requestBody
   * @returns AsyncFunctionResponse OK
   * @throws ApiError
   */
  public static submitCustomerKybInformation(
    requestBody: KybRequest
  ): CancelablePromise<AsyncFunctionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/customer/kyb",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
      },
    });
  }

  /**
   * Get customer creation state
   * @returns OnboardingStateResponse OK
   * @throws ApiError
   */
  public static getOnboardingState(): CancelablePromise<OnboardingStateResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/onboarding-state",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `No customers found`,
      },
    });
  }

  /**
   * Get existing customer ID
   * Check whether a customer already exists by using domain
   * @param domain
   * @returns CustomerExistsResponse OK
   * @throws ApiError
   */
  public static getCustomerExistsByDomain(
    domain: string
  ): CancelablePromise<CustomerExistsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customer/exists",
      query: {
        domain: domain,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Get details about a customer
   * Returns details about a customer, requires that the user is a member of that customer.
   * @param customerId
   * @returns CustomerDetailResponse OK
   * @throws ApiError
   */
  public static getCustomerById(
    customerId: string
  ): CancelablePromise<CustomerDetailResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customer/{customerId}",
      path: {
        customerId: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Get all users against a particular customerID
   * Get all users against a particular customerID
   * @param customerId
   * @returns CustomerUserCollection OK
   * @throws ApiError
   */
  public static getUsersForCustomer(
    customerId: string
  ): CancelablePromise<CustomerUserCollection> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customer/{customerId}/users",
      path: {
        customerId: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Remove a user from a customer
   * Remove a user from a customer
   * @param customerId
   * @param userId
   * @returns void
   * @throws ApiError
   */
  public static removeUserFromCustomer(
    customerId: string,
    userId: string
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/customer/{customerId}/user/{userId}",
      path: {
        customerId: customerId,
        userId: userId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Get a list of order limits for a customer
   * Get a list of order limits for a customer
   * @param customerId
   * @returns CustomerOrderLimitsResponse OK
   * @throws ApiError
   */
  public static getCustomerOrderLimits(
    customerId: string
  ): CancelablePromise<CustomerOrderLimitsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customer/{customerId}/limits",
      path: {
        customerId: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Set the order limits for a customer
   * Set the order limits for a customer
   * @param customerId
   * @param requestBody
   * @returns CustomerOrderLimitsResponse OK
   * @throws ApiError
   */
  public static setCustomerOrderLimits(
    customerId: string,
    requestBody: CustomerOrderLimitsSetRequest
  ): CancelablePromise<CustomerOrderLimitsResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/customer/{customerId}/limits",
      path: {
        customerId: customerId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Set the enable balances flag for a customer
   * Set the enable balances flag for a customer
   * @param customerId
   * @param requestBody
   * @returns AsyncFunctionResponse OK
   * @throws ApiError
   */
  public static enableBalances(
    customerId: string,
    requestBody: EnableBalancesRequest
  ): CancelablePromise<AsyncFunctionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/customer/{customerId}/enable-balances",
      path: {
        customerId: customerId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Gets a signed shop URL for a Shop for Runa site.
   * Gets a signed shop URL for a Shop for Runa site.
   * @param customerId
   * @returns SignedUrlResponse OK
   * @throws ApiError
   */
  public static getSignedShopUrl(
    customerId: string
  ): CancelablePromise<SignedUrlResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/customer/{customerId}/get-signed-shop-url",
      path: {
        customerId: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Get list of customers with minimal data based on search query
   * Get list of customers with minimal data based on search query
   * @param search
   * @returns CustomerSearchListResponse OK
   * @throws ApiError
   */
  public static getCustomersListBySearch(
    search: string
  ): CancelablePromise<CustomerSearchListResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customers",
      query: {
        search: search,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Get a list of feature flags and their values for a customer.
   * Get a list of feature flags and their values for a customer.
   * @param customerId
   * @returns CustomerFeatures OK
   * @throws ApiError
   */
  public static getCustomerFeatureFlags(
    customerId: string
  ): CancelablePromise<CustomerFeatures> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/customer/{customerId}/features",
      path: {
        customerId: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Set the feature flags and their values for a customer.
   * Set the feature flags and their values for a customer. Accepts partial updates. Only allow_manual_brand_approval is supported for now.
   * @param customerId
   * @param requestBody
   * @returns CustomerFeatures OK
   * @throws ApiError
   */
  public static setCustomerFeatureFlags(
    customerId: string,
    requestBody: CustomerFeaturesPartialUpdate
  ): CancelablePromise<CustomerFeatures> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/customer/{customerId}/features",
      path: {
        customerId: customerId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        422: `ValidationErrorResponse`,
      },
    });
  }
}
